import React from 'react';
import { Box, Typography, Paper, CircularProgress, List, ListItem, ListItemText, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    height: '100%',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#e6eaf0',
    borderRadius: 18,
    boxSizing: 'border-box',
  },
  drawer: {
    width: 280,
    flexShrink: 0,
    backgroundColor: '#f7faff',
    borderRadius: 18,
    border: '1px solid #dce8f5',
    padding: theme.spacing(1.5),
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
    boxShadow: '0 10px 24px rgba(0, 45, 85, 0.08)',
  },
  drawerTitle: {
    color: '#002D55',
    fontWeight: 700,
    padding: theme.spacing(1, 2),
    fontSize: 'clamp(0.9rem, 0.95vw, 1.05rem)',
    borderBottom: '1px solid #dce8f5',
    marginBottom: theme.spacing(1),
  },
  listItem: {
    borderRadius: 14,
    marginBottom: theme.spacing(0.5),
    cursor: 'pointer',
    border: '1px solid transparent',
    '&:hover': {
      backgroundColor: '#edf5fc',
      borderColor: '#dce8f5',
    },
  },
  listItemSelected: {
    backgroundColor: '#ffffff !important',
    border: '1px solid #dce8f5',
    boxShadow: '0 8px 20px rgba(0, 45, 85, 0.08)',
    '& $listPrimaryText': {
      color: '#FF7F27',
    },
    '&:hover': {
      backgroundColor: '#ffffff !important',
    },
  },
  listPrimaryText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#002D55',
  },
  listSecondaryText: {
    fontSize: '0.75rem',
    color: '#64748B',
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 300px)',
    minWidth: 0,
  },
  section: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f7faff',
    borderRadius: 18,
    border: '1px solid #dce8f5',
    boxShadow: 'none',
  },
  sectionTitle: {
    color: '#FF7F27',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontSize: 'clamp(0.76rem, 0.82vw, 0.92rem)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  content: {
    color: '#324158',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: 'clamp(0.72rem, 0.8vw, 0.88rem)',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#64748B',
  },
  statusHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0.5, 0.5, 0),
  },
  statusHeaderText: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
  },
  statusCounter: {
    fontSize: 'clamp(0.62rem, 0.7vw, 0.81rem)',
    fontWeight: 700,
    color: '#002D55',
    backgroundColor: '#f5f5f5',
    border: '1px solid #dddddd',
    borderRadius: 14,
    padding: '4px 12px',
    whiteSpace: 'nowrap',
  },
}));

const StatusRapportView = ({ selectedPatient, userToken }) => {
  const classes = useStyles();
  const [statusReportList, setStatusReportList] = React.useState([]);
  const [selectedReport, setSelectedReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchStatusReport = async () => {
      if (!selectedPatient || !selectedPatient.treatment_id || !userToken) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(
          `https://api.virtus-one.com/journal/status-report/${selectedPatient.treatment_id}/list/`,
          {
            headers: {
              'Authorization': `Token ${userToken}`,
              'Accept': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch status report: ${response.status}`);
        }

        const data = await response.json();
        // Sort by most recent first (assuming created_at or updated_at field)
        const sortedData = Array.isArray(data) 
          ? data.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
          : [];
        
        setStatusReportList(sortedData);
        // Default to the most recent status report
        setSelectedReport(sortedData.length > 0 ? sortedData[0] : null);
      } catch (err) {
        console.error('[StatusRapportView] Error fetching status report:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStatusReport();
  }, [selectedPatient?.treatment_id, userToken]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">Error loading status report: {error}</Typography>
      </Box>
    );
  }

  if (statusReportList.length === 0) {
    return (
      <Box p={2}>
        <Paper className={classes.section}>
          <Typography className={classes.emptyState}>
            No status report available for this treatment.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.drawer}>
        <Typography className={classes.drawerTitle}>
          Status Reports ({statusReportList.length})
        </Typography>
        <Divider />
        <List component="nav">
          {statusReportList.map((report, index) => (
            <ListItem
              key={report.id || index}
              className={`${classes.listItem} ${selectedReport?.id === report.id ? classes.listItemSelected : ''}`}
              onClick={() => setSelectedReport(report)}
            >
              <ListItemText
                primary={
                  <Typography className={classes.listPrimaryText}>
                    Report #{statusReportList.length - index}
                  </Typography>
                }
                secondary={
                  <Typography className={classes.listSecondaryText}>
                    {formatDate(report.updated_at || report.created_at)}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box className={classes.contentArea}>
        <Box className={classes.statusHeader}>
          <Typography className={classes.statusHeaderText}>
            Status Rapport
          </Typography>
          <span className={classes.statusCounter}>
            {statusReportList.length} report{statusReportList.length === 1 ? '' : 's'}
          </span>
        </Box>
        {selectedReport && (
          <>
            {selectedReport.metrics_summary && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Status Summary
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedReport.metrics_summary}
                </Typography>
              </Paper>
            )}

            {selectedReport.baseline_comparison && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Baseline vs Current Status
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedReport.baseline_comparison}
                </Typography>
              </Paper>
            )}

            {selectedReport.compliance_report && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Compliance Report
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedReport.compliance_report}
                </Typography>
              </Paper>
            )}

            {selectedReport.physiotherapist_notes && (
              <Paper className={classes.section}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Physiotherapist Notes
                </Typography>
                <Typography variant="body2" className={classes.content}>
                  {selectedReport.physiotherapist_notes}
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default StatusRapportView;
